import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import useDeviceType from '../../../customHooks/usedeviceType';
import './style.scss';

const TOAST_ROOT_ID = 'toast-root';

// This component represents a SINGLE Toast item
const Toast = ({
    type = 'info',
    position = 'top-right',
    isOpen = false,
    onClose,
    onExited,
    message,
    title,
    autoClose = true,
    duration = 3000
}) => {
    const [isRendered, setIsRendered] = useState(false);
    const [animationClass, setAnimationClass] = useState('');
    // Pause the auto-dismiss timer while the user hovers or keyboard-focuses the
    // toast, so it can be read at the user's pace (WCAG 2.2.1 / 2.2.2).
    const [isPaused, setIsPaused] = useState(false);
    const containerRef = useRef(null);

    const deviceType = useDeviceType();
    const effectivePosition = deviceType === 'mobile' ? 'top-center' : position;

    // Handle container creation/retrieval
    // We need to ensure we are appending to the SAME container for the same position
    useEffect(() => {
        const root = document.getElementById(TOAST_ROOT_ID);
        if (!root) return;

        const containerId = `zelp-toast-container-${effectivePosition}`;
        let container = document.getElementById(containerId);

        if (!container) {
            container = document.createElement('div');
            container.id = containerId;
            container.className = `zelp-toast-container zelp-toast-container__${effectivePosition}`;
            root.appendChild(container);
        }

        containerRef.current = container;
    }, [effectivePosition]);

    // Handle open/close animation and mounting
    useEffect(() => {
        let timer;
        if (isOpen) {
            setIsRendered(true);
            if (effectivePosition === 'top-center') {
                setAnimationClass('zelp-toast-enter-top');
            } else {
                const isRight = effectivePosition.includes('right');
                setAnimationClass(isRight ? 'zelp-toast-enter-right' : 'zelp-toast-enter-left');
            }
        } else if (isRendered) { // Only animate out if currently rendered
            if (effectivePosition === 'top-center') {
                setAnimationClass('zelp-toast-exit-top');
            } else {
                const isRight = effectivePosition.includes('right');
                setAnimationClass(isRight ? 'zelp-toast-exit-right' : 'zelp-toast-exit-left');
            }

            timer = setTimeout(() => {
                setIsRendered(false);
                if (onExited) onExited();
            }, 400); // Match CSS animation duration (updated to 0.4s)
        }
        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [isOpen, effectivePosition, isRendered, onExited]);

    // Handle auto-close (paused while hovered/focused)
    useEffect(() => {
        let timer;
        if (isOpen && autoClose && onClose && !isPaused) {
            timer = setTimeout(() => {
                onClose();
            }, duration);
        }
        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [isOpen, autoClose, duration, onClose, isPaused]);

    const getIcon = () => {
        // Using 'zelpicon' font family for all icons as requested
        // Mappings based on variables.scss:
        // zelpicon-done: success (checkmark)
        // zelpicon-close: error/warning (x mark) - or zelpicon-warning for explicit warning icon
        // zelpicon-information: info

        switch (type) {
            case 'warning':
                // Using 'zelpicon-close' (or similar X icon) for warning as requested previously to use error icon
                // and rotating it if needed, similar to previous implementation but with zelpicon family.
                // 'zelpicon-warning' exists but is an exclamation mark.
                // If the request "use error icon for warning as well" still stands, we use the X.
                // 'zelpicon-close' is \e9022.
                return <i className="zelpicon zelpicon-issue" aria-hidden="true" />;
            case 'success':
                return <i className="zelpicon zelpicon-done" aria-hidden="true" />;
            case 'error':
                return <i
                    className="zelpicon zelpicon-issue"
                    aria-hidden="true" />; // Using zelpicon-close for error (X)
            case 'info':
            default:
                return <i className="zelpicon zelpicon-information" aria-hidden="true" />;
        }
    };

    if (!isRendered || !containerRef.current) return null;

    const isAssertive = type === 'error' || type === 'warning';

    return createPortal(
        <div
            className={`zelp-toast zelp-toast__${type} ${animationClass}`}
            role={isAssertive ? 'alert' : 'status'}
            aria-live={isAssertive ? 'assertive' : 'polite'}
            aria-atomic="true"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onFocus={() => setIsPaused(true)}
            onBlur={() => setIsPaused(false)}>
            <div className="zelp-toast_content">
                <div className="zelp-toast_text">
                    <div className="zelp-toast_text_title">
                        <span className="zelp-toast_icon">
                            {getIcon()}
                        </span>
                        {title}
                    </div>
                    <div className="zelp-toast_text_message">{message}</div>
                </div>
            </div>
            <div className="zelp-toast_close">
                <button onClick={onClose} type="button" className="zelp-toast_close" aria-label="Close toast">
                    <i className="zelpicon zelpicon-delete-cancel" aria-hidden="true" />
                </button>
            </div>
        </div>,
        containerRef.current
    );
};

export default Toast;
